import { useRef, useState, useCallback } from 'react';
import { IDynamicFormRef } from '@src/components';

export function useDynamicFormState() {
    const formRef = useRef<IDynamicFormRef>(null);
    const [isDirty, setIsDirty] = useState(false);
    const [isValid, setIsValid] = useState(false);
    const [isValidating, setIsValidating] = useState(false);

    // Ref to track the latest validation call
    const latestValidationId = useRef(0);

    const handleDataChange = useCallback(async (changed: boolean) => {
        setIsDirty(changed);

        if (!changed || !formRef.current) {
            setIsValid(false);
            return;
        }

        setIsValidating(true);
        const validationId = ++latestValidationId.current;

        try {
            const valid = await formRef.current.trigger();
            // Only update state if this is the latest validation request
            if (validationId === latestValidationId.current) {
                setIsValid(valid);
            }
        } finally {
            if (validationId === latestValidationId.current) {
                setIsValidating(false);
            }
        }
    }, []);

    const handleSubmit = useCallback(
        async (onSubmit: (values: any) => void) => {
            if (!formRef.current) return;

            const valid = await formRef.current.trigger();
            if (!valid) return;

            const values = formRef.current.getValues();
            onSubmit(values);
        },
        []
    );

    return {
        formRef,
        isDirty,
        isValid,
        isValidating,
        handleDataChange,
        handleSubmit,
    };
}
